import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import { Lock, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isHardcodedUser, HARDCODED_USER } from '../config/hardcodedUser';
import env from '../config/env';
import SetupLicense from '../components/SetupLicense';
import SetupWizard from '../components/SetupWizard';

function Login() {
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [fjalekalimi, setFjalekalimi] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [shopName, setShopName] = useState('');
  const [licenseVerified, setLicenseVerified] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // Kontrollo nëse është setup mode - direkt me backend
  useEffect(() => {
    const checkSetupMode = async () => {
      try {
        const response = await apiService.get('/check-setup');
        const { is_setup_needed } = response;
        
        if (is_setup_needed) {
          // Nëse setup është i nevojshëm, shfaq license screen
          setIsSetupMode(true);
          setLicenseVerified(false);
        } else {
          // Nëse nuk ka nevojë për setup, shfaq login normal
          setIsSetupMode(false);
          setLicenseVerified(true); // Nuk na duhet license nëse nuk është setup mode
        }
      } catch (error) {
        // Nëse ka error, supozojmë që setup është i nevojshëm
        console.error('Error checking setup mode:', error);
        setIsSetupMode(true);
        setLicenseVerified(false);
      } finally {
        setCheckingSetup(false);
      }
    };

    checkSetupMode();
  }, []);

  // Fetch settings për logo dhe emër (vetëm nëse nuk është setup mode)
  useEffect(() => {
    if (isSetupMode || checkingSetup) return;

    const fetchSettings = async () => {
      try {
        const data = await apiService.get('/settings');
        setSettings(data);
        
        // Set shop name
        if (data?.emri_duqanit) {
          setShopName(data.emri_duqanit);
        }
        
        // Set logo URL
        if (data?.logo_url) {
          // Nëse logo_url tashmë është URL e plotë, përdor direkt
          if (data.logo_url.startsWith('http://') || data.logo_url.startsWith('https://')) {
            setLogoUrl(data.logo_url);
          } else {
            // Nëse është relative path, shto base URL
            const baseUrl = env.API_URL_Image || env.API_URL || '';
            let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
            // Nëse baseUrl nuk përfundon me /public, shto /public
            if (!cleanBaseUrl.endsWith('/public')) {
              cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
            }
            setLogoUrl(`${cleanBaseUrl}${data.logo_url.startsWith('/') ? '' : '/'}${data.logo_url}`);
          }
        } else if (data?.logo_path) {
          if (data.logo_path.startsWith('http://') || data.logo_path.startsWith('https://')) {
            setLogoUrl(data.logo_path);
          } else {
            const baseUrl = env.API_URL_Image || env.API_URL || '';
            let cleanBaseUrl = baseUrl.replace(/\/api$/, '');
            // Nëse baseUrl nuk përfundon me /public, shto /public
            if (!cleanBaseUrl.endsWith('/public')) {
              cleanBaseUrl = cleanBaseUrl.replace(/\/public$/, '') + '/public';
            }
            setLogoUrl(`${cleanBaseUrl}/storage/${data.logo_path}`);
          }
        }
      } catch (error) {
        console.error('Error fetching settings for login:', error);
        // Përdor defaults nëse ka gabim
      }
    };

    fetchSettings();
  }, [isSetupMode, checkingSetup]);

  const handleLicenseVerified = () => {
    // Pas verifikimit të license, shfaq setup wizard
    setLicenseVerified(true);
    setIsSetupMode(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Kontrollo nëse është hardcoded user
      if (isHardcodedUser(username, fjalekalimi)) {
        // Për hardcoded user, bëj API call me credentials
        const response = await apiService.post('/login', { 
          username: HARDCODED_USER.username, 
          fjalekalimi: HARDCODED_USER.password 
        });
        
        const { token, emri, roli, lokacion_id, is_principal } = response;
        login(token, emri || HARDCODED_USER.name, roli || HARDCODED_USER.role, lokacion_id, is_principal ?? false);
        
        // Principal user shkon në dashboard
        navigate('/');
      } else {
        // Normal login flow
        const response = await apiService.post('/login', { username, fjalekalimi });
        const { token, emri, roli, lokacion_id, is_principal } = response;
        login(token, emri, roli, lokacion_id, is_principal ?? false);

        if (roli === 'admin') {
          navigate('/');
        } else if (roli === 'shites') {
          navigate('/shitje');
        } else {
          navigate('/kerkimi-stok');
        }
      }
    } catch (err) {
      logger.error("Gabim nga login API:", err);
      setError(err.message || 'Përdoruesi ose fjalëkalim i pasaktë.');
    } finally {
      setLoading(false);
    }
  };

  // Nëse po kontrollon setup mode, shfaq loading
  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-900 dark:to-dark-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Duke kontrolluar statusin e sistemit...</p>
        </div>
      </div>
    );
  }

  // Nëse license nuk është verifikuar, shfaq license screen
  if (!licenseVerified) {
    return <SetupLicense onVerified={handleLicenseVerified} />;
  }

  // Nëse është setup mode, shfaq setup wizard
  if (isSetupMode) {
    return <SetupWizard />;
  }

  // Nëse nuk është setup mode, shfaq login normal
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-logo dark:bg-gradient-to-br dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden transition-colors duration-300">
      {/* Background decorative elements - Logo Colors */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob ${
          theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-300'
        }`}></div>
        <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000 ${
          theme === 'dark' ? 'bg-secondary-500/20' : 'bg-secondary-300'
        }`}></div>
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000 ${
          theme === 'dark' ? 'bg-primary-400/20' : 'bg-primary-200'
        }`}></div>
      </div>

      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>

      <div className="relative z-10 w-full max-w-md px-4 animate-fade-in">
        <div className={`glass dark:glass-dark rounded-3xl shadow-premium-xl dark:shadow-premium-xl-dark p-8 md:p-10`}>
          {/* Logo */}
          <div className="flex flex-col items-center mb-8 animate-slide-down">
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-gradient-logo rounded-2xl blur-lg opacity-50 dark:opacity-30"></div>
              <img
                src={logoUrl}
                alt={shopName}
                className="relative w-20 h-auto drop-shadow-lg object-contain"
                onError={(e) => {
                  if (e.target.src !== '/logo.png') {
                    e.target.src = '/logo.png';
                  }
                }}
              />
            </div>
            <h1 className="text-2xl font-bold text-gradient-logo mb-2">{shopName}</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Kyçu për të vazhduar</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 animate-slide-down bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg shadow-sm">
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6 animate-slide-up">
            <div>
              <label className="form-label">
                Përdoruesi
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" size={20} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Shkruaj përdoruesin"
                  className="input-premium pl-12"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">
                Fjalëkalimi
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" size={20} />
                <input
                  type="password"
                  value={fjalekalimi}
                  onChange={(e) => setFjalekalimi(e.target.value)}
                  placeholder="Shkruaj fjalëkalimin"
                  className="input-premium pl-12"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full text-base py-3.5"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Po kyçet...
                </span>
              ) : (
                'Kyçu'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
